import React, { useState, useRef } from 'react';
import { RoutinePreview } from './RoutinePreview';
import { RoutineTimer } from './RoutineTimer';
import { RoutineReport } from './RoutineReport';
import { SessionAttentionCapture } from '../../../SessionAttentionCapture';
import { PageContainer, PageHeader } from '../../../layout/Page';
import { useRoutineStore } from '../../../../stores/routineStore';
import { useRoutineSessionStore } from '../../../../stores/routineSessionStore';
import { useRoutineSessionLogStore } from '../../../../stores/routineSessionLogStore';
import type { Routine } from '../../../../types';
import type { SessionFocusCapture } from '../../../../lib/sessionFocusCapture';

interface RoutineExecutionProps {
  routine: Routine;
  onClose: () => void;
}

type ExecutionStage = 'preview' | 'active' | 'attention' | 'complete';

const formatDuration = (seconds: number) => {
  const m = Math.round(seconds / 60);
  if (m < 60) return `${m}m`;
  return `${Math.floor(m / 60)}h ${m % 60}m`;
};

export function RoutineExecution({ routine, onClose }: RoutineExecutionProps) {
  // Hover-Play launches with autoStart → skip the preview, begin the timer at once.
  const autoStart = useRoutineSessionStore(s => s.autoStart);
  const [stage, setStage] = useState<ExecutionStage>(autoStart ? 'active' : 'preview');
  const [earnedXP, setEarnedXP] = useState(0);
  const [timeSpent, setTimeSpent] = useState(0); // actual elapsed seconds across the run
  const [xpByDomain, setXpByDomain] = useState<Record<string, number>>({});
  const startedAtRef = useRef<string | null>(autoStart ? new Date().toISOString() : null);
  /** The in-flight insert. Held, not awaited, so the rating prompt appears at once
   *  while the run is already being recorded — see `handleComplete`. */
  const loggedIdRef = useRef<Promise<string | null> | null>(null);
  const { completeRoutine } = useRoutineStore();
  const expand = useRoutineSessionStore(s => s.expand);
  const logSession = useRoutineSessionLogStore(s => s.logSession);
  const rateSession = useRoutineSessionLogStore(s => s.rateSession);

  const handleStart = () => {
    startedAtRef.current = new Date().toISOString();
    setStage('active');
  };

  const handleComplete = (
    finalXP: number,
    totalTimeSpent: number,
    domainXP: Record<string, number>,
    realDomainXP: Record<string, number>,
  ) => {
    setEarnedXP(finalXP);
    setTimeSpent(totalTimeSpent);
    setXpByDomain(domainXP); // full cosmetic breakdown → report rings
    completeRoutine(routine.id);

    // Record the run to history. The last published runtime snapshot tells us how
    // far we got (read before the timer unmounts); fall back to the full count.
    const reached = useRoutineSessionStore.getState().runtime?.stepIndex;
    const stepsCompleted = reached != null
      ? Math.min(reached + 1, routine.steps.length)
      : routine.steps.length;
    // Fire the insert NOW and only hold its promise. Awaiting it would delay the
    // report on a network round-trip; deferring it until after the rating prompt
    // would risk losing the run (and its domainXp) if the prompt is abandoned —
    // and `reached` above is read off runtime state that dies with the timer.
    loggedIdRef.current = logSession({
      routineId: routine.id,
      startedAt: startedAtRef.current ?? new Date(Date.now() - totalTimeSpent * 1000).toISOString(),
      durationSeconds: totalTimeSpent,
      xpEarned: finalXP, // headline (cosmetic) total for history
      domainXp: realDomainXP, // ONLY the break/non-completing XP that credits real levels
      stepsCompleted,
    });

    // The run may have finished while minimized — surface the report.
    expand();
    setStage('attention');
  };

  const handleAttentionSubmit = async (focus: SessionFocusCapture) => {
    setStage('complete'); // don't make the user wait on the write
    // By now the insert has long since resolved — the user spent seconds rating.
    const id = await loggedIdRef.current;
    loggedIdRef.current = null;
    if (id) await rateSession(id, focus);
  };

  const handleAttentionSkip = () => {
    loggedIdRef.current = null;
    setStage('complete');
  };

  const handleNext = () => {
    // This will be called between steps
    // You can add any between-step logic here
  };

  // No back affordance mid-run (you pause/skip/minimize from the timer card); back
  // returns to the routine on the preview and after the completion report.
  const backLink = stage === 'active' ? undefined : { label: 'Back to routine', onClick: onClose };

  return (
    <PageContainer>
      <PageHeader title={routine.name} compact backLink={backLink} />

      {stage === 'preview' && (
        <RoutinePreview routine={routine} onStart={handleStart} />
      )}

      {stage === 'active' && (
        <RoutineTimer
          routine={routine}
          onComplete={handleComplete}
          onNext={handleNext}
        />
      )}

      {stage === 'attention' && (
        <SessionAttentionCapture
          title={routine.name}
          subtitle={formatDuration(timeSpent)}
          onSubmit={handleAttentionSubmit}
          onSkip={handleAttentionSkip}
        />
      )}

      {stage === 'complete' && (
        <RoutineReport
          routine={routine}
          earnedXP={earnedXP}
          timeSpentSeconds={timeSpent}
          xpByDomain={xpByDomain}
          onClose={onClose}
        />
      )}
    </PageContainer>
  );
}
